import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { shopConfig as cfg } from '../shopConfig';
import { useBrand } from '../hooks/useBrand';
import { useCart } from '../hooks/useCart';
import { useCategories } from '../hooks/useCategories';
import { useCollections } from '../hooks/useCollections';
import { useAuth } from '../hooks/useAuth';
import { hourRows } from '../hours';

// -----------------------------------------------------------------------------
// Layout.tsx - the SmartShop storefront shell.
//
// Lifted from Glen's proven Essaness design (two-tier sticky header, shimmer
// logo, gradient wordmark, open/closed live indicator, conversion buttons,
// slide-in mobile drawer, DBOS footer, cookie banner) and made TENANT-AGNOSTIC:
// every shop value comes from shopConfig + the live SmartBrand hook. Rebuilt
// MOBILE-FIRST (62% of Red Robin's visitors are mobile): the mobile layout is
// the primary design, desktop is the enhancement.
//
// Nav is DATA-DRIVEN (decision B): primary nav = the tenant's distinct product
// categories (derived live from the catalogue via useCategories); a separate
// horizontal scroll-snap strip shows the collections (Kaffe, Rant & Rave, ...).
// No hand-curated category tree.
//
// House rule: short hyphen "-" only.
// -----------------------------------------------------------------------------

interface LayoutProps {
  children: React.ReactNode;
}

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

// Where a nav entry points. "Shop" and "More" are NOT real groups - More is the
// remainder bucket holding live categories that no config group claims - so
// /group/more renders "0 items" while the categories listed inside it have stock.
// Both resolve to the full shop instead. One definition, used by the desktop
// dropdown header, the top-level anchor and the mobile drawer, so the three cannot
// drift apart again.
const groupPath = (group: string, path?: string) =>
  path ? path : (group === 'Shop' || group === 'More') ? '/shop' : `/group/${slug(group)}`;

// ---- Open/Closed live indicator (Design Bible 5.2) --------------------------
function useOpenState() {
  const { hours, timezone } = cfg.contact;
  const now = new Date(new Date().toLocaleString('en-US', { timeZone: timezone }));
  const day = now.getDay();
  const mins = now.getHours() * 60 + now.getMinutes();
  const today = hours[day];
  const isOpen = today !== null && today !== undefined && mins >= today.open && mins < today.close;
  return isOpen;
}

// ---- Shimmer + animation styles (brand-colour driven) -----------------------
const BrandStyles: React.FC<{ accent: string }> = ({ accent }) => (
  <style>{`
    @keyframes shimmer { 0% { background-position:-200% center } 100% { background-position:200% center } }
    @keyframes cartGlow {
      0%,100% { box-shadow: 0 0 0 1px ${accent}55, 0 0 10px ${accent}66 }
      50%     { box-shadow: 0 0 0 1px ${accent}88, 0 0 18px ${accent}aa }
    }
    .brand-logo { transition: filter .3s; filter: drop-shadow(0 2px 8px rgba(0,0,0,.35)); }
    .brand-logo:hover { filter: drop-shadow(0 2px 16px ${accent}cc) brightness(1.08); }
    .shimmer-wrap { position:relative; display:inline-flex; overflow:hidden; border-radius:4px }
    .shimmer-wrap::after {
      content:''; position:absolute; inset:0;
      background:linear-gradient(105deg, transparent 30%, rgba(255,255,255,.22) 50%, transparent 70%);
      background-size:200% 100%; animation:shimmer 3.5s ease-in-out infinite; pointer-events:none;
    }
    /* scroll-snap collections strip */
    .coll-strip { display:flex; gap:8px; overflow-x:hidden;
      padding:10px 16px; scrollbar-width:none; }
    .coll-strip::-webkit-scrollbar { display:none }
    .coll-pill { scroll-snap-align:start; flex:0 0 auto; }
    /* responsive: mobile-first. desktop nav appears >=861px */
    #desk-nav { display:none }
    #cat-scroll { display:flex }
    @media(min-width:861px){
      #desk-nav { display:flex }
      #burger { display:none !important }
      #cat-scroll { display:none }   /* categories go into the desktop nav row instead */
    }
    #coll-mini-wrap { display:none }
    @media(min-width:861px){ #coll-mini-wrap { display:flex } #coll-band-mobile { display:none } }
        @media(max-width:860px){ #top-info-bar { display:none } #brand-wordmark { display:none } #coll-mini-wrap { display:none !important } #top-info-name { display:none !important } }
  `}</style>
);

// ---- Logo ------------------------------------------------------------------
const Logo: React.FC<{ size?: number }> = ({ size = 44 }) => (
  <div className="shimmer-wrap" style={{ flexShrink: 0 }}>
    <img src={cfg.brand.logo_url} alt={`${cfg.shop_name} logo`} className="brand-logo"
      style={{ height: size, width: 'auto', display: 'block' }} />
  </div>
);

// ---- Top info bar (desktop only): open/closed + call + directions -----------
const TopInfoBar: React.FC<{ accent: string }> = ({ accent }) => {
  const isOpen = useOpenState();
  const [hoursOpen, setHoursOpen] = useState(false);
  const statusColor = isOpen ? '#2d9e2d' : accent;
  const glow = isOpen ? 'rgba(45,158,45,.6)' : `${accent}99`;
  const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${cfg.contact.maps_destination}`;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 18, flex: 1, justifyContent: 'center', minWidth: 0 }}>
      <span
        style={{ display: 'flex', alignItems: 'center', gap: 6, position: 'relative', cursor: 'default' }}
        onMouseEnter={() => setHoursOpen(true)}
        onMouseLeave={() => setHoursOpen(false)}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor, boxShadow: `0 0 6px 2px ${glow}` }} />
        <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: statusColor, borderBottom: '1px dotted rgba(255,255,255,.35)' }}>
          {isOpen ? 'Open Now' : 'Closed'}
        </span>
        {hoursOpen && (
          <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: 8, background: '#161616', border: '1px solid rgba(255,255,255,.14)', boxShadow: '0 8px 32px rgba(0,0,0,.6)', padding: '12px 14px', zIndex: 600, minWidth: 212, textAlign: 'left', cursor: 'default' }}>
            <div style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.12em', color: 'rgba(255,255,255,.5)', marginBottom: 8 }}>
              Opening Hours
            </div>
            {hourRows().map((h) => (
              <div key={h.day} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '3px 0', fontSize: 11, color: h.today ? 'white' : 'rgba(255,255,255,.65)', fontWeight: h.today ? 700 : 400 }}>
                <span>{h.day}</span>
                <span style={{ color: h.closed ? 'rgba(255,255,255,.35)' : 'inherit', whiteSpace: 'nowrap' }}>{h.text}</span>
              </div>
            ))}
          </div>
        )}
      </span>
      {cfg.contact.phone && (
        <a href={`tel:${cfg.contact.phone}`} style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.1em', color: 'white', textDecoration: 'none', fontWeight: 700, background: accent, padding: '4px 10px', whiteSpace: 'nowrap' }}>
          Call Now
        </a>
      )}
      <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
        style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, textTransform: 'uppercase', letterSpacing: '.1em', color: 'white', textDecoration: 'none', fontWeight: 700, padding: '4px 10px', background: '#4285F4', borderRadius: 2, whiteSpace: 'nowrap' }}>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="white"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1112 6a2.5 2.5 0 010 5.5z"/></svg>
        Directions
      </a>
    </div>
  );
};

// ---- Content pages in the main nav (Phase-3 seam) ----------------------------
// Reads shop_config.pages[] once the CMS surface exists; falls back to the current
// page set until then. Adding a page later becomes a config edit, not a code edit.
const NAV_PAGES: { label: string; path: string }[] =
  (cfg as any).pages || [{ label: 'Our Story', path: '/our-story' }, { label: 'Contact', path: '/contact' }];

// ---- Desktop category nav (>=861px): hover cascade, optional 3rd level --------
const DesktopNav: React.FC<{ groups: { group: string; categories: string[]; subcategories?: { [c: string]: string[] } }[]; extra: string[] }> = ({ groups, extra }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState<string | null>(null);
  const [openCat, setOpenCat] = useState<string | null>(null);
  const items: { group: string; categories: string[]; path?: string }[] = [{ group: 'Shop', categories: [] as string[] }, ...groups];
  NAV_PAGES.forEach((p) => items.push({ group: p.label, categories: [], path: p.path }));
  if (extra.length) items.push({ group: 'More', categories: extra });

  return (
    <nav id="desk-nav" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
      {items.map((it) => {
        const hasSub = it.categories.length > 0;
        const subMap = (it as any).subcategories || {};
        return (
          <div key={it.group} style={{ position: 'relative' }}
            onMouseEnter={() => setOpen(it.group)}
            onMouseLeave={(e) => { const r = e.relatedTarget as Node; if (!e.currentTarget.contains(r)) { setOpen(null); setOpenCat(null); } }}>
            <a onClick={() => navigate(groupPath(it.group, it.path))}
              style={{ display: 'block', padding: '0 15px', height: 46, lineHeight: '46px', fontSize: 13, fontWeight: 800, fontStyle: 'italic', fontFamily: 'Georgia, serif', letterSpacing: '.02em', color: open === it.group ? 'white' : 'rgba(255,255,255,.85)', textDecoration: 'none', whiteSpace: 'nowrap', cursor: 'pointer', transition: 'color .2s' }}>
              {it.group}{hasSub ? '  \u25be' : ''}
            </a>
            {hasSub && open === it.group && (
              <div style={{ position: 'absolute', top: '100%', left: 0, background: '#161616', border: '1px solid rgba(255,255,255,.12)', zIndex: 300, minWidth: 230, boxShadow: '0 8px 32px rgba(0,0,0,.5)' }}>
                <a onClick={() => navigate(groupPath(it.group))}
                  style={{ display: 'block', padding: '11px 18px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'rgba(255,255,255,.9)', textDecoration: 'none', borderBottom: '1px solid rgba(255,255,255,.1)', cursor: 'pointer' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,.08)'; setOpenCat(null); }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
                  All {it.group}
                </a>
                {it.categories.map((c) => {
                  const subs: string[] = subMap[c] || [];
                  const hasCatSub = subs.length > 0;
                  return (
                    <div key={c} style={{ position: 'relative' }}
                      onMouseEnter={() => setOpenCat(hasCatSub ? c : null)}>
                      <a onClick={() => navigate(`/shop/${slug(c)}`)}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '11px 18px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em', color: openCat === c ? 'white' : 'rgba(255,255,255,.75)', textDecoration: 'none', borderBottom: '1px solid rgba(255,255,255,.05)', cursor: 'pointer', fontWeight: 500, background: openCat === c ? 'rgba(255,255,255,.08)' : 'none' }}>
                        {c}{hasCatSub ? <span style={{ opacity: .5, fontSize: 10 }}>{'\u25b6'}</span> : null}
                      </a>
                      {hasCatSub && openCat === c && (
                        <div style={{ position: 'absolute', left: '100%', top: -1, background: '#0d0f0d', border: '1px solid rgba(255,255,255,.15)', zIndex: 500, minWidth: 210, boxShadow: '8px 8px 32px rgba(0,0,0,.7)' }}>
                          {subs.map((s) => (
                            <a key={s} onClick={() => navigate(`/shop/${slug(s)}`)}
                              style={{ display: 'block', padding: '11px 18px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em', color: 'rgba(255,255,255,.75)', textDecoration: 'none', borderBottom: '1px solid rgba(255,255,255,.05)', cursor: 'pointer', fontWeight: 500 }}
                              onMouseEnter={e => { e.currentTarget.style.color = 'white'; e.currentTarget.style.background = 'rgba(255,255,255,.08)'; }}
                              onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,.75)'; e.currentTarget.style.background = 'none'; }}>
                              {s}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};

// ---- Desktop-only collections mini-strip: Stripe-style marquee.
// Default: constant slow drift right->left. Hover middle: stops (click a logo).
// Hover near an edge: scrolls that way, soft-accelerating toward the border.
// Leave: resumes slow drift. List is duplicated for a seamless loop; the second
// copy is aria-hidden so crawlers see each collection name once (SEO).
const CollectionsMini: React.FC<{ collections: { name: string; logo_url?: string | null }[]; accent: string }> = ({ collections, accent }) => {
  const navigate = useNavigate();
  const trackRef = React.useRef<HTMLDivElement>(null);
  const rafRef = React.useRef<number | null>(null);
  const posRef = React.useRef(0);
  const modeRef = React.useRef<{ hover: boolean; edge: number }>({ hover: false, edge: 0 });

  React.useEffect(() => {
    const track = trackRef.current;
    if (!track || !collections.length) return;
    const DRIFT = 0.3;
    const tick = () => {
      const half = track.scrollWidth / 2;
      const m = modeRef.current;
      if (m.hover && m.edge !== 0) posRef.current += m.edge;
      else if (!m.hover) posRef.current += DRIFT;
      if (half > 0) {
        if (posRef.current >= half) posRef.current -= half;
        else if (posRef.current < 0) posRef.current += half;
      }
      track.style.transform = `translateX(${-posRef.current}px)`;
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [collections.length]);

  const onMove = (e: React.MouseEvent) => {
    const el = e.currentTarget as HTMLElement;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    const EDGE = 0.28;
    let edge = 0;
    if (x < EDGE) edge = -((EDGE - x) / EDGE) * 5;
    else if (x > 1 - EDGE) edge = ((x - (1 - EDGE)) / EDGE) * 5;
    modeRef.current = { hover: true, edge };
  };
  const onLeave = () => { modeRef.current = { hover: false, edge: 0 }; };

  if (!collections.length) return null;
  const doubled = [...collections, ...collections];

  return (
    <div onMouseMove={onMove} onMouseLeave={onLeave} style={{ maxWidth: 240, overflow: 'hidden' }}>
      <div ref={trackRef} style={{ display: 'flex', gap: 8, alignItems: 'center', width: 'max-content', willChange: 'transform' }}>
        {doubled.map((c, i) => {
          const first = i < collections.length;
          return (
            <button key={`${c.name}-${i}`} onClick={() => navigate(`/collections/${slug(c.name)}`)}
              title={c.name} aria-label={c.name} aria-hidden={first ? undefined : true}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', flex: '0 0 auto', padding: 0, height: 32, display: 'flex', alignItems: 'center' }}>
              {c.logo_url
                ? <img src={c.logo_url} alt={first ? c.name : ''} style={{ height: 32, width: 'auto', objectFit: 'contain', borderRadius: 6, background: '#fff', padding: 2 }} />
                : <span style={{ color: accent, fontSize: 12, fontWeight: 800 }}>{c.name}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ---- Collections strip: horizontal logo tiles (wheel scrolls sideways) -------
const CollectionsStrip: React.FC<{ collections: { name: string; logo_url?: string | null }[]; accent: string }> = ({ collections, accent }) => {
  const navigate = useNavigate();
  const ref = React.useRef<HTMLDivElement>(null);
  const rafRef = React.useRef<number | null>(null);
  const pausedRef = React.useRef(false);
  const resumeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    const el = ref.current;
    if (!el || !collections.length) return;
    const DRIFT = 0.3;
    const tick = () => {
      const half = el.scrollWidth / 2;
      if (!pausedRef.current) el.scrollLeft += DRIFT;
      if (half > 0 && el.scrollLeft >= half) el.scrollLeft -= half;
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); if (resumeTimer.current) clearTimeout(resumeTimer.current); };
  }, [collections.length]);

  const pause = () => { pausedRef.current = true; if (resumeTimer.current) clearTimeout(resumeTimer.current); };
  const resumeSoon = () => { if (resumeTimer.current) clearTimeout(resumeTimer.current); resumeTimer.current = setTimeout(() => { pausedRef.current = false; }, 1500); };

  if (!collections.length) return null;
  const doubled = [...collections, ...collections];

  return (
    <div style={{ background: cfg.brand.secondary, borderTop: '1px solid rgba(255,255,255,.06)' }}>
      <div ref={ref} className="coll-strip"
        onTouchStart={pause} onTouchEnd={resumeSoon}
        onPointerDown={pause} onPointerUp={resumeSoon}
        style={{ overflowX: 'hidden' }}>
        {doubled.map((c, i) => {
          const first = i < collections.length;
          const initials = c.name.split(/[^a-zA-Z0-9]+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
          return (
            <button key={`${c.name}-${i}`} className="coll-pill" onClick={() => navigate(`/collections/${slug(c.name)}`)}
              title={c.name} aria-label={c.name} aria-hidden={first ? undefined : true}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: 4 }}>
              <span style={{
                width: 64, height: 64, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden', flexShrink: 0,
                background: c.logo_url ? '#fff' : 'rgba(255,255,255,.04)',
                border: `1px solid ${accent}`,
              }}>
                {c.logo_url
                  ? <img src={c.logo_url} alt={first ? c.name : ''} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  : <span style={{ color: accent, fontSize: 18, fontWeight: 800, letterSpacing: '.02em' }}>{initials}</span>}
              </span>
              <span style={{ color: 'rgba(255,255,255,.75)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', whiteSpace: 'nowrap' }}>{c.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ---- Mobile slide-in drawer (grouped, expandable) ---------------------------
const MobileDrawer: React.FC<{ open: boolean; onClose: () => void; groups: { group: string; categories: string[] }[]; extra: string[]; accent: string }> = ({ open, onClose, groups, extra, accent }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<string | null>(null);
  const go = (p: string) => { navigate(p); onClose(); };
  const items: { group: string; categories: string[]; path?: string }[] = [...groups];
  NAV_PAGES.forEach((p) => items.push({ group: p.label, categories: [], path: p.path }));
  if (extra.length) items.push({ group: 'More', categories: extra });

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 400, pointerEvents: open ? 'auto' : 'none' }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.7)', opacity: open ? 1 : 0, transition: 'opacity .25s' }} />
      <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: '86%', maxWidth: 360, background: cfg.brand.secondary, overflowY: 'auto', transform: open ? 'translateX(0)' : 'translateX(-100%)', transition: 'transform .28s cubic-bezier(.4,0,.2,1)', boxShadow: '4px 0 32px rgba(0,0,0,.6)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px', borderBottom: '1px solid rgba(255,255,255,.08)' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Logo size={32} />
            <span style={{ color: accent, fontSize: 18, fontWeight: 900, fontStyle: 'italic' }}>{cfg.shop_name}</span>
          </span>
          <button onClick={onClose} aria-label="Close menu" style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.6)', fontSize: 22, cursor: 'pointer', minWidth: 44, minHeight: 44 }}>&times;</button>
        </div>
        <button onClick={() => go('/shop')} style={{ width: '100%', textAlign: 'left', padding: '16px 24px', background: 'none', border: 'none', borderBottom: '1px solid rgba(255,255,255,.06)', color: 'white', fontSize: 13, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.12em', cursor: 'pointer' }}>All Products</button>
        {items.map((it) => {
          const hasSub = it.categories.length > 0;
          return (
            <div key={it.group} style={{ borderBottom: '1px solid rgba(255,255,255,.05)' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <button onClick={() => go(groupPath(it.group, it.path))}
                  style={{ flex: 4, textAlign: 'left', padding: '15px 24px', background: 'none', border: 'none', color: 'white', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', cursor: 'pointer' }}>
                  {it.group}
                </button>
                {hasSub && (
                  <button onClick={() => setExpanded(expanded === it.group ? null : it.group)}
                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '15px 0', background: 'none', border: 'none', borderLeft: '1px solid rgba(255,255,255,.07)', color: 'rgba(255,255,255,.55)', fontSize: 16, cursor: 'pointer' }}>
                    {expanded === it.group ? '\u25b4' : '\u25be'}
                  </button>
                )}
              </div>
              {hasSub && expanded === it.group && it.categories.map((c) => (
                <button key={c} onClick={() => go(`/shop/${slug(c)}`)}
                  style={{ width: '100%', textAlign: 'left', padding: '12px 24px 12px 36px', background: '#141414', border: 'none', borderTop: '1px solid rgba(255,255,255,.04)', color: 'rgba(255,255,255,.7)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em', cursor: 'pointer' }}>
                  {c}
                </button>
              ))}
            </div>
          );
        })}
        <div style={{ padding: 22 }}>
          <button onClick={() => go('/shop')} style={{ width: '100%', background: accent, color: cfg.brand.secondary, border: 'none', padding: 14, fontWeight: 800, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.12em', cursor: 'pointer' }}>Shop Online</button>
        </div>
      </div>
    </div>
  );
};

// ---- Footer -----------------------------------------------------------------
const Footer: React.FC<{ accent: string }> = ({ accent }) => {
  const navigate = useNavigate();
  return (
    <footer style={{ background: cfg.brand.secondary, padding: '16px 24px 12px', borderTop: '1px solid rgba(255,255,255,.06)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Logo size={64} />
          <div>
            <p style={{ color: accent, fontWeight: 900, fontStyle: 'italic', fontSize: 18, margin: '0 0 2px' }}>{cfg.shop_name}</p>
            <p style={{ color: 'rgba(255,255,255,.4)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '.1em', margin: 0 }}>
              {cfg.tagline}{cfg.established ? ` | ${cfg.established}` : ''}
            </p>
            {cfg.contact.address_lines.map((l, i) => (
              <p key={i} style={{ color: 'rgba(255,255,255,.3)', fontSize: 10, margin: '2px 0 0' }}>{l}</p>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center' }}>
          {cfg.footer.legal_links.map(([label, path]) => (
            <a key={label} onClick={() => navigate(path)}
              style={{ color: 'rgba(255,255,255,.55)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.1em', textDecoration: 'none', cursor: 'pointer' }}
              onMouseEnter={e => (e.currentTarget.style.color = cfg.brand.cream)}
              onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,.55)')}>
              {label}
            </a>
          ))}
        </div>
        <div>
          <p style={{ color: 'rgba(255,255,255,.35)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '.08em', margin: '0 0 4px' }}>Powered by</p>
          <a href="https://dbos.world" target="_blank" rel="noopener noreferrer" style={{ color: accent, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', textDecoration: 'none' }}>{cfg.footer.powered_by}</a>
          <p style={{ color: 'rgba(255,255,255,.25)', fontSize: 9, textTransform: 'uppercase', letterSpacing: '.08em', margin: '4px 0 0' }}>Ireland · {cfg.gido_id}</p>
        </div>
      </div>
      <div style={{ borderTop: '1px solid rgba(255,255,255,.07)', paddingTop: 8 }}>
        <p style={{ color: 'rgba(255,255,255,.3)', fontSize: 9, textTransform: 'uppercase', letterSpacing: '.1em', margin: 0 }}>© {new Date().getFullYear()} {cfg.shop_name} · Powered by DBOS · dbos.world</p>
      </div>
    </footer>
  );
};

// ---- Cookie banner (SmartConsent-style, Design Bible 4) ---------------------
const CookieBanner: React.FC<{ accent: string }> = ({ accent }) => {
  const [visible, setVisible] = useState(() => !localStorage.getItem('cookie_consent'));
  if (!visible) return null;
  const set = (v: string) => { localStorage.setItem('cookie_consent', v); setVisible(false); };
  return (
    <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: cfg.brand.secondary, borderTop: '1px solid rgba(255,255,255,.1)', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 1000, gap: 16, flexWrap: 'wrap' }}>
      <p style={{ fontSize: 12, color: 'rgba(255,255,255,.6)', margin: 0, flex: 1, minWidth: 260 }}>
        We use essential cookies for shop functionality. Analytics cookies are only used with your consent.{' '}
        <a href="/cookies" style={{ color: accent, textDecoration: 'none' }}>Cookie Policy</a>
      </p>
      <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
        <button onClick={() => set('essential')} style={{ background: 'rgba(255,255,255,.08)', color: 'rgba(255,255,255,.6)', border: '1px solid rgba(255,255,255,.15)', padding: '8px 16px', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '.1em', cursor: 'pointer' }}>Essential Only</button>
        <button onClick={() => set('all')} style={{ background: accent, color: cfg.brand.secondary, border: 'none', padding: '8px 16px', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '.1em', cursor: 'pointer' }}>Accept All</button>
      </div>
    </div>
  );
};

// ---- Layout -----------------------------------------------------------------
export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const brand = useBrand();                       // live SmartBrand -> colours, falls back to cfg
  const accent = brand.accent || cfg.brand.accent;
  const { cart, loadCart } = useCart();
  const { signedIn } = useAuth();
  const liveCategories = useCategories();         // distinct categories present in the catalogue
  // Config lists the brand tiles and their logos; the catalogue decides which of them
  // have anything to sell. 11 of her 13 brands had sold through and were still on the
  // homepage, clickable, landing on "0 items" - the same dead end the category groups
  // already guard against, one axis over. Matched on slug because the route matches on
  // slug (ShopPage: collections[].some(slugify(c) === filterSlug)), so a config name
  // written "Hope & Ivy" still lines up with the product's value.
  //
  // Until the live set has loaded we show nothing rather than guessing - sessionStorage
  // makes that invisible after the first page of a visit, and an empty strip for a
  // moment is better than a tile that lies.
  //
  // STAGE 2 (Phase 3): collections move out of this repo into shop_config in Firestore
  // with per-collection auto | always | hidden, so the owner decides which brands show -
  // including one she stocks in-store but not online. Config in a repo means only we
  // can change it, which is the actual problem behind this symptom.
  const liveCollections = useCollections();
  const collections = React.useMemo(() => {
    const liveSet = new Set(liveCollections.map(slug));
    return (cfg.collections || []).filter(c => liveSet.has(slug(c.name)));
  }, [liveCollections]);
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Build the grouped nav from config, but only include categories that actually
  // exist in the live catalogue (so an empty group is hidden). Any live category
  // not assigned to any group becomes "extra" -> the "More" menu (nothing lost).
  const { groups, extra } = React.useMemo(() => {
    const cfgGroups = cfg.category_groups || [];
    const liveSet = new Set(liveCategories);
    const assigned = new Set<string>();
    const groups = cfgGroups
      .map(g => {
        const cats = g.categories.filter(c => liveSet.has(c));
        cats.forEach(c => assigned.add(c));
        return { group: g.group, categories: cats, subcategories: (g as any).subcategories };
      })
      .filter(g => g.categories.length > 0);   // a group with nothing live in it is not nav, it is a dead end
    const extra = liveCategories.filter(c => !assigned.has(c));
    return { groups, extra };
  }, [liveCategories]);

  useEffect(() => { loadCart(); }, [loadCart, location.pathname]);
  useEffect(() => {
    const h = () => loadCart();
    window.addEventListener('cart:changed', h);
    return () => window.removeEventListener('cart:changed', h);
  }, [loadCart]);
  const hasItems = (cart?.item_count ?? 0) > 0;

  return (
    <div style={{ minHeight: '100vh', background: cfg.brand.cream, fontFamily: 'system-ui,-apple-system,sans-serif', display: 'flex', flexDirection: 'column' }}>
      <BrandStyles accent={accent} />

      <header style={{ position: 'sticky', top: 0, zIndex: 200 }}>
        {/* top info bar - desktop only */}
        <div id="top-info-bar" style={{ background: cfg.brand.cream, borderBottom: '1px solid rgba(0,0,0,.08)', padding: '6px 24px', display: 'grid', gridTemplateColumns: 'auto 1fr', alignItems: 'center', gap: 16 }}>
        <span id="top-info-name" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.12em', color: 'rgba(0,0,0,.4)', whiteSpace: 'nowrap' }}>{cfg.shop_name} · {cfg.tagline}</span>
          <TopInfoBar accent={accent} />
        </div>

        {/* brand bar */}
        <div style={{ background: `linear-gradient(180deg, #232323 0%, ${cfg.brand.secondary} 45%, #050505 100%)`, boxShadow: '0 4px 24px rgba(0,0,0,.55)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px', height: 62, borderBottom: '1px solid rgba(255,255,255,.06)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button id="burger" onClick={() => setMobileOpen(true)} aria-label="Open menu"
                style={{ minWidth: 44, minHeight: 44, background: 'none', border: 'none', cursor: 'pointer', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, display: 'flex' }}>
                {[0, 1, 2].map(i => <span key={i} style={{ width: 24, height: 2, background: 'rgba(255,255,255,.75)' }} />)}
              </button>
              <a onClick={() => { navigate('/'); window.scrollTo(0, 0); }} style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', cursor: 'pointer' }}>
                <Logo size={42} />
                <span id="brand-wordmark" style={{ fontSize: 'clamp(16px, 5vw, 30px)', fontWeight: 900, fontStyle: 'italic', letterSpacing: '-.5px', background: `linear-gradient(180deg, ${accent} 0%, ${accent}bb 50%, ${accent} 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,.8))', whiteSpace: 'nowrap' }}>{cfg.shop_name}</span>
              </a>
            </div>
            <div id="coll-mini-wrap" style={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: 0, padding: '0 16px' }}>
              <CollectionsMini collections={collections} accent={accent} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button onClick={() => navigate('/shop')} style={{ background: accent, color: cfg.brand.secondary, border: 'none', padding: '9px 18px', fontWeight: 800, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.1em', cursor: 'pointer' }}>Shop</button>
              <button onClick={() => navigate('/cart')} style={{ background: hasItems ? `${accent}22` : 'none', color: 'white', border: hasItems ? `1px solid ${accent}` : '1px solid rgba(255,255,255,.3)', animation: hasItems ? 'cartGlow 2s ease-in-out infinite' : 'none', padding: '9px 18px', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.1em', cursor: 'pointer', transition: 'all .3s' }}>
                Cart{hasItems ? ` (${cart!.item_count})` : ''}
              </button>
              <button onClick={() => navigate('/account')}
                aria-label={signedIn ? 'My account' : 'Sign in'}
                title={signedIn ? 'My account' : 'Sign in'}
                style={{ background: 'none', color: 'white', border: signedIn ? `1px solid ${accent}` : '1px solid rgba(255,255,255,.3)', padding: '7px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .3s' }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={signedIn ? accent : 'rgba(255,255,255,.85)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </button>
            </div>
          </div>
          {/* collections band - MOBILE ONLY (desktop shows the mini-strip in the title row) */}
          <div id="coll-band-mobile"><CollectionsStrip collections={collections} accent={accent} /></div>
          {/* desktop category nav row - grouped */}
          <div style={{ padding: '0 20px' }}><DesktopNav groups={groups} extra={extra} /></div>
        </div>
      </header>

      <MobileDrawer open={mobileOpen} onClose={() => setMobileOpen(false)} groups={groups} extra={extra} accent={accent} />
      <CookieBanner accent={accent} />

      <main style={{ flex: 1 }}>{children}</main>

      <Footer accent={accent} />
    </div>
  );
};
